import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { ApiService } from '../utils/api';
import { formatCurrency } from '../utils/currency';
import { normalizeEmail } from '../utils/coupon';

const CouponApplyField = ({
  eventId,
  email,
  formData,
  currency = 'INR',
  quote,
  onApplied,
  onRemoved,
}) => {
  const [code, setCode] = useState(quote?.code || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const handleApply = async () => {
    setError('');
    const trimmed = (code || '').trim();
    if (!trimmed) {
      setError('Enter a coupon code');
      return;
    }
    const normalizedEmail = normalizeEmail(email);
    if (!normalizedEmail) {
      setError('Enter your email before applying a coupon');
      return;
    }
    setBusy(true);
    try {
      const result = await ApiService.applyCouponPublic(eventId, {
        code: trimmed,
        email: normalizedEmail,
        formData: formData || {},
      });
      onApplied?.(result);
    } catch (e) {
      setError(e?.message || 'Could not apply coupon');
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async () => {
    setError('');
    setBusy(true);
    try {
      if (quote?.holdToken) {
        await ApiService.removeCouponPublic(eventId, quote.holdToken);
      }
      setCode('');
      onRemoved?.();
    } catch (e) {
      setError(e?.message || 'Could not remove coupon');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-blue-100 bg-blue-50/60 rounded-lg p-4 space-y-3 md:col-span-2">
      <Label className="text-sm font-medium text-gray-800">Coupon code</Label>
      {quote ? (
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-3">
            <p className="text-sm font-semibold text-green-800">Applied: {quote.code}</p>
            <Button type="button" variant="outline" size="sm" onClick={handleRemove} disabled={busy}>
              Remove
            </Button>
          </div>
          <div className="text-sm text-gray-700 space-y-1">
            <div className="flex justify-between"><span>Original</span><span>{formatCurrency(quote.originalAmount, currency)}</span></div>
            <div className="flex justify-between text-green-800"><span>Discount</span><span>-{formatCurrency(quote.discountAmount, currency)}</span></div>
            <div className="flex justify-between font-semibold"><span>Payable</span><span>{formatCurrency(quote.finalAmount, currency)}</span></div>
          </div>
        </div>
      ) : (
        <div className="flex gap-2">
          <Input
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="Enter code"
            className="bg-white"
          />
          <Button type="button" onClick={handleApply} disabled={busy}>
            {busy ? 'Applying...' : 'Apply'}
          </Button>
        </div>
      )}
      {error && <p className="text-xs text-red-600">{error}</p>}
    </div>
  );
};

export default CouponApplyField;
